package com.isaias.fyattravel.web.pages.payment

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.theme.ThemeColors

/**
 * Payment Failed Page
 * Shows after failed or cancelled PesaPal payment
 */
@Composable
fun PaymentFailedPage(onNavigate: (String) -> Unit) {
    val productId = window.localStorage.getItem("pesapal_product_id") ?: ""
    val locale = WebLocaleStore.locale.value

    Div({
        style {
            maxWidth(600.px)
            property("margin", "0 auto")
            padding(3.em, 1.em)
            textAlign("center")
        }
    }) {
        // Failed icon
        Div({
            style {
                width(100.px)
                height(100.px)
                borderRadius(50.percent)
                backgroundColor(ThemeColors.errorBg)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("margin", "0 auto 2em")
            }
        }) {
            Span({
                style {
                    fontSize(3.em)
                    color(ThemeColors.error)
                }
            }) { Text("✕") }
        }

        H1({
            style {
                fontSize(2.em)
                fontWeight("800")
                color(ThemeColors.textPrimary)
                marginBottom(0.5.em)
            }
        }) { Text(WebStrings.t(WebStringKey.PAYMENT_FAILED_TITLE)) }

        P({
            style {
                fontSize(1.1.em)
                color(ThemeColors.textMuted)
                marginBottom(2.em)
            }
        }) {
            Text(WebStrings.t(WebStringKey.PAYMENT_FAILED_MESSAGE))
        }

        // Suggestions card
        Div({
            style {
                backgroundColor(ThemeColors.primaryMuted)
                backgroundColor(ThemeColors.primaryMuted)
                padding(1.5.em)
                borderRadius(1.em)
                textAlign("left")
                marginBottom(2.em)
                property("border", "1px solid ${ThemeColors.primary}")
            }
        }) {
            H2({
                style {
                    fontSize(1.1.em)
                    fontWeight("700")
                    color(ThemeColors.warning)
                    marginBottom(1.em)
                }
            }) { Text(WebStrings.t(WebStringKey.PAYMENT_FAILED_SUGGESTIONS_TITLE)) }

            listOf(
                WebStrings.t(WebStringKey.PAYMENT_FAILED_SUGGESTION_1),
                WebStrings.t(WebStringKey.PAYMENT_FAILED_SUGGESTION_2),
                WebStrings.t(WebStringKey.PAYMENT_FAILED_SUGGESTION_3),
                WebStrings.t(WebStringKey.PAYMENT_FAILED_SUGGESTION_4)
            ).forEach { suggestion ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(0.75.em)
                        marginBottom(0.75.em)
                        alignItems(AlignItems.Start)
                    }
                }) {
                    Span({ style { color(ThemeColors.warning) } }) { Text("•") }
                    Span({ style { color(ThemeColors.textSecondary); fontSize(0.95.em) } }) { Text(suggestion) }
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(1.em)
                justifyContent(JustifyContent.Center)
                property("flex-wrap", "wrap")
            }
        }) {
            // Try again
            if (productId.isNotBlank()) {
                Button({
                    style {
                        backgroundColor(ThemeColors.primary)
                        color(ThemeColors.textOnGold)
                        padding(0.875.em, 1.5.em)
                        borderRadius(0.75.em)
                        border(0.px)
                        cursor("pointer")
                        fontWeight("700")
                        fontSize(1.em)
                    }
                    onClick { onNavigate("/checkout?productId=$productId") }
                }) {
                    Text(WebStrings.t(WebStringKey.TRY_AGAIN))
                }
            }

            // WhatsApp
            Button({
                    style {
                        backgroundColor(ThemeColors.whatsapp)
                        color(ThemeColors.textOnDark)
                        padding(0.875.em, 1.5.em)
                        borderRadius(0.75.em)
                        border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    fontSize(1.em)
                }
                onClick { 
                    val msg = WebStrings.format(locale, WebStringKey.PAYMENT_FAILED_WHATSAPP_MESSAGE, productId)
                    val encoded = js("encodeURIComponent")(msg) as String
                    window.open("https://wa.me/256758250625?text=$encoded", "_blank")
                }
            }) {
                Text(WebStrings.t(WebStringKey.CONTACT_VIA_WHATSAPP))
            }

            Button({
                style {
                    backgroundColor(ThemeColors.surface)
                    color(ThemeColors.textPrimary)
                    padding(0.875.em, 1.5.em)
                    borderRadius(0.75.em)
                    property("border", "1px solid ${ThemeColors.divider}")
                    cursor("pointer")
                    fontWeight("700")
                    fontSize(1.em)
                }
                onClick { onNavigate("/") }
            }) {
                Text(WebStrings.t(WebStringKey.GO_TO_HOME))
            }
        }

        // Contact info
        P({
            style {
                marginTop(2.em)
                fontSize(0.9.em)
                color(ThemeColors.textSecondary)
            }
        }) {
            Text(WebStrings.t(WebStringKey.NEED_HELP_CONTACT))
        }
    }
}
